/** /zh/decision-log-template — Chinese version of the decision-log-template
 *  landing page. Native zh content, same structure as
 *  DecisionLogTemplate.tsx. */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function DecisionLogTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/decision-log-template"
      hreflangLocales={['en', 'zh', 'zhtw']}
      documentTitle="决策日志模板（不会过时的那一种）· Draftlize"
      documentDescription="一份可以直接抄走的决策日志模板——决策、背景、备选方案、理由、依赖、状态、负责人、日期。但要知道：写在 Notion 或 markdown 里的模板，填完那一刻就开始过时。在 Draftlize 里每个字段都是一张结构化卡片，上游决策一改，所有依赖它的卡片自动标记 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 决策日志</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            一份会自己更新的<br/>
            <em>决策日志模板。</em>
          </h1>
          <p className="lede">
            你是来找<em>决策日志模板</em>的——字段、标题、能直接粘进文档的东西。它就在下面，拿走。但要清楚你拿到的是什么：模板是一套填空格式，而你填完的那一瞬间，它就开始过时了。Notion 表格也好、markdown 文件也好，没有任何东西把一个决策连到依赖它的那些 spec 上；于是决策一变，日志就悄悄变成了虚构。Draftlize 给你同样的八个字段——并把每一个都变成 AI agent 能读也能写的卡片，一个决策一动，下游的一切都会自动把自己标记成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">这份模板</span>
            <h2 className="serif">八个字段。<em>随便抄到哪都行。</em></h2>
          </div>
          <p>这就是整份模板。放在文档、wiki 或 markdown 文件里都成——它和架构决策记录（ADR）是同一种形状。直接用。下一节讲的是：当每个字段不再是文字、而成为一张卡片时，会发生什么变化。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '决策 Decision',
              hint: '必填',
              body: '这个决定，一句话讲清。「我们按量收费，不按席位收费。」如果你压缩不成一句话，说明你还没真正做出决定。',
            },
            {
              k: '背景 Context',
              hint: '为什么是现在',
              body: '逼出这个选择的处境。当时什么是成立的、什么被约束住了、是什么压力让它成了一个「决策」而不是一个默认值。',
            },
            {
              k: '备选 Options considered',
              hint: '那些分叉',
              body: '真正认真考虑过的备选，不是用来陪衬的稻草人。决策日志的价值大半在这里——它能拦住你，别去重新争论一条你早已带着理由否决掉的路。',
            },
            {
              k: '理由 Rationale',
              hint: '凭什么',
              body: '为什么是这个方案赢、其它输。这是你未来真正会打开日志去看的唯一一个字段。诚实写下你接受了什么样的取舍。',
            },
            {
              k: '依赖 Dependencies',
              hint: '什么压在它上面',
              body: '那些假设了这个决策成立的 spec、决策和工作。在文档里，这是一份你迟早会忘记更新的清单。它是决定整份日志还真不真的那个字段。',
            },
            {
              k: '状态 Status',
              hint: '当前状态',
              body: '提议中、已采纳、已被取代——或者，stale。决策不是永恒的；日志必须能说出「这条已经不成立了」，又不抹掉它当初为什么成立的那段历史。',
            },
            {
              k: '负责人 Owner',
              hint: '谁',
              body: '当理由开始讲不通时，去问谁。不是为了追责——是为了半年后那个追问。',
            },
            {
              k: '日期 Date',
              hint: '何时',
              body: '什么时候定的。记录起来很便宜，却是让你能按事情真实发生的顺序去读这些决策的那样东西。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.25 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.08em',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          模板就是这些。往下：为什么「填完」正是它开始腐烂的那一刻——以及我们对此做了什么。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么模板会<em>过时。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">模板是一种格式，不是一套系统</h3>
            <p>这八个字段是好的——正因如此，ADR 从 2011 年起用的差不多就是这个形状。但一种格式只把决策捕捉一次。它根本不知道自己记下的东西什么时候不再成立，因为一张文字表格没法盯着产品的其余部分一起变。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">「依赖」字段是一个你注定会失信的承诺</h3>
            <p>你老老实实列出了什么依赖这个决策。然后决策变了，更新那些依赖就成了一场手动考古——凭记忆，找出每一个假设了旧决定的 spec。没人会真去做。那个本该让日志保持诚实的字段，是第一个开始撒谎的。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">「填完」就是最高水位线</h3>
            <p>模板最准的时刻，是你刚填完它的那一分钟，此后再也没那么准过。到第九十天，里头有相当一部分是错的，而你分不清是哪一部分——于是整份日志被悄悄地不再信任，这比没有还糟。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份模板，<em>变成活的。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个字段成为一张卡片，而不是一个单元格</h3>
            <p>在 Draftlize 里，模板不是一张空表——而是一张带着这些字段的结构化卡片：决策、背景、备选、理由、依赖、状态、负责人、日期。结构化、可按 ID 寻址、能从一份 spec 或一个 thread 里被引用，而不是埋在某个 wiki 页里。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个决策，依赖自动标 stale</h3>
            <p>因为「依赖」是真实的连接、而不是一串名词，改掉一个决策，就会让每张压在它上面的卡片自动变 stale——就像构建系统让一个改动文件的所有下游失效一样。模板许下的承诺，substrate 替它兑现。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 能读它，也能写它</h3>
            <p>Claude Code 或 Cursor，经 MCP，在起草下一份 spec 前读完每一个相关决策，并把新决策按同样的形状写回去。日志不再是一份写给「永不回来的读者」的文档，而成了每一轮都会被读到的上下文。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          模板告诉你该写下什么。它没法告诉你，你写下的东西什么时候已经不再成立。
        </blockquote>
        <cite>留下这八个字段。让 substrate 替你保持它们最新。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是决策日志？', a: '决策日志是团队所做重要选择的流水记录——记下做了什么、为什么这么做、否掉了什么，好让推理活过那场会议，也让人半年后不用再重新争一个早已定下的决定。每条通常包含：决策、背景、考虑过的选项、理由、依赖、状态、负责人和日期。' },
            { q: '决策日志和 ADR 有什么区别？', a: 'ADR（架构决策记录）是专门用于工程选择的决策日志——同样的八字段形状，范围更窄。决策日志还涵盖产品、定价、策略层面的决定。如果你已经在写 ADR，决策日志就是把同一套纪律用到架构之上的那些决策上。' },
            { q: '每条决策日志该记哪些字段？', a: '决策（一句话讲清这个选择）、背景（为什么是现在）、考虑过的选项、理由（为什么是它赢）、依赖（有什么靠着它）、状态（提议 / 采纳 / 被取代 / 过期）、负责人、日期。理由和被否掉的选项，是你未来真正会打开日志去读的两个字段。' },
            { q: '决策日志放哪里？为什么大多数都会过期？', a: '放任何能装文字的地方——wiki、markdown 文件、Notion 表格。它们过期，是因为没有东西把一条记下的决策连到依赖它的 spec，所以决策一变，日志就悄悄变成了小说。Draftlize 把同样的八个字段做成卡片，决策一动，就自动把每个依赖它的东西标记为过期——日志自己更新，而不是烂掉。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始你的决策日志。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把上面这份模板拿走，或者建一个项目、让 agent 用你已有的文档把它填起来——然后替你把每个决策、以及一切依赖它们的东西，保持最新。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
      <TemplateCta template="decision-log" locale="zh" />
    </MarketingShell>
  );
}
